import { Link, Outlet, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import {
    ChatBubbleLeftIcon,
    ChevronRightIcon,
    CpuChipIcon,
    CurrencyDollarIcon,
    UsersIcon,
    DocumentTextIcon,
    CreditCardIcon,
} from '@heroicons/react/24/outline';

interface NavItem {
    label: string;
    path: string;
    icon: typeof CpuChipIcon;
    description: string;
}

const adminNavItems: NavItem[] = [
    {
        label: 'Users',
        path: '/admin/users',
        icon: UsersIcon,
        description: 'View and manage user accounts and credit balances',
    },
    {
        label: 'Transactions',
        path: '/admin/transactions',
        icon: DocumentTextIcon,
        description: 'View all credit transactions across all users',
    },
    {
        label: 'Purchases',
        path: '/admin/purchases',
        icon: CreditCardIcon,
        description: 'View all Stripe purchases and payment statuses',
    },
    {
        label: 'Feedback',
        path: '/admin/feedback',
        icon: ChatBubbleLeftIcon,
        description: 'View and manage user feedback submissions',
    },
];

const devToolsNavItems: NavItem[] = [
    {
        label: 'Circuit Breaker',
        path: '/admin/dev/circuit-breaker',
        icon: CpuChipIcon,
        description: 'Test circuit breaker behavior with simulated API calls',
    },
    {
        label: 'Exchange Rates',
        path: '/admin/dev/exchange-rates',
        icon: CurrencyDollarIcon,
        description: 'Test currency conversion with circuit breaker protection',
    },
];

function NavCard({ item }: { item: NavItem }) {
    const Icon = item.icon;
    return (
        <Link
            to={item.path}
            className="group flex items-start gap-4 rounded-lg border border-gray-300 bg-white p-6 transition-all hover:border-accent hover:shadow-md dark:border-gray-700 dark:bg-secondary dark:hover:border-primary-light"
        >
            <div className="rounded-lg bg-gray-100 p-3 group-hover:bg-accent group-hover:text-white dark:bg-dark dark:group-hover:bg-primary-light">
                <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                        {item.label}
                    </h3>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 transition-transform group-hover:translate-x-1 dark:text-gray-500" />
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {item.description}
                </p>
            </div>
        </Link>
    );
}

export function AdminPage() {
    const location = useLocation();
    const isAdminRoot = location.pathname === '/admin';

    return (
        <Layout>
            <div className="space-y-6">
                <div className="space-y-1">
                    <PageHeader title="Admin" />
                    <p className="text-gray-600 dark:text-gray-400">
                        Administrative tools and dev utilities
                    </p>
                </div>

                {isAdminRoot ? (
                    <div className="space-y-8">
                        {/* Admin Management */}
                        <div>
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Management
                            </h2>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {adminNavItems.map((item) => (
                                    <NavCard key={item.path} item={item} />
                                ))}
                            </div>
                        </div>

                        {/* Dev Tools */}
                        <div>
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Dev Tools
                            </h2>
                            <div className="grid gap-4 md:grid-cols-2">
                                {devToolsNavItems.map((item) => (
                                    <NavCard key={item.path} item={item} />
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <Outlet />
                )}
            </div>
        </Layout>
    );
}
